import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, Mail } from "lucide-react";
import { Toaster } from "sonner";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import CallNowButton from "../components/CallNowButton";
import { POSTS } from "../data/blog";

const easeExpo = [0.16, 1, 0.3, 1];

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
}

export default function BlogList() {
  useEffect(() => {
    document.title = "Blog | Print & Marketing Insights | GM Print Brisbane";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-list-page">
      <Navigation />
      <CallNowButton />

      <main className="pt-32">
        {/* Header */}
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: easeExpo }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="w-14 h-px bg-black" />
            <span className="label-caps">The Journal</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: easeExpo }}
            className="font-display text-6xl md:text-7xl lg:text-8xl leading-[0.9] mb-6"
            data-testid="blog-headline"
          >
            Notes on print<br />&amp; brand growth.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: easeExpo }}
            className="font-body text-lg md:text-xl text-black/70 max-w-2xl"
          >
            Field notes, playbooks, and unfiltered thinking on Brisbane print, signage, and marketing — from the team at GM Print.
          </motion.p>
        </section>

        {/* Posts */}
        <section className="bg-[#F5F5F5] border-y-2 border-black">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
            {POSTS.length === 0 ? (
              <div className="max-w-2xl mx-auto text-center py-20" data-testid="blog-empty-state">
                <div className="cmyk-bar cmyk-bar-thick w-24 mx-auto mb-10">
                  <div className="c" /><div className="m" /><div className="y" /><div className="k" />
                </div>
                <h2 className="font-display text-4xl md:text-5xl mb-5">First article inbound.</h2>
                <p className="font-body text-lg text-black/70 mb-8 leading-relaxed">
                  Sarah's writing up her first field notes — a mix of Brisbane print insights and small business marketing playbooks. New posts land here monthly.
                </p>
                <a
                  href="mailto:quotes@gmprint.com.au?subject=Notify me when the blog launches"
                  className="btn-primary"
                  data-testid="blog-notify-btn"
                >
                  <Mail size={16} /> Get notified
                </a>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {POSTS.map((post, i) => (
                  <motion.article
                    key={post.slug}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.7, delay: i * 0.06, ease: easeExpo }}
                    data-testid={`blog-post-card-${post.slug}`}
                  >
                    <Link to={`/blog/${post.slug}`} className="blog-card group">
                      {post.image && (
                        <div className="blog-card-image">
                          <img src={post.image} alt={post.title} />
                        </div>
                      )}
                      <div className="blog-card-body">
                        <div className="flex items-center gap-3 text-xs font-mono text-black/50 mb-3">
                          <span className="flex items-center gap-1"><Calendar size={11} /> {formatDate(post.date)}</span>
                          <span>·</span>
                          <span className="flex items-center gap-1"><Clock size={11} /> {post.readTime}</span>
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl mb-3 leading-tight">{post.title}</h3>
                        <p className="font-body text-sm text-black/70 leading-relaxed mb-5 line-clamp-3">{post.excerpt}</p>
                        <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
                          Read article <ArrowRight size={14} />
                        </span>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <Toaster position="bottom-right" richColors />
    </div>
  );
}
